/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import type { Breadcrumb } from "@sentry/react";

/**
 * U-13: the `beforeBreadcrumb` hook installed by main.tsx's `Sentry.init`.
 *
 * A breadcrumb is the one place a value that was carefully kept out of the
 * response body and out of the error message can still leave the machine.
 * Sentry's default integration set includes `breadcrumbs`, which captures
 * console calls and fetch/XHR metadata; passing an `integrations` array adds
 * to the defaults rather than replacing them, so that integration is on.
 *
 * Two rules:
 *
 *  1. Every console breadcrumb is dropped. Nothing in `src` writes a console
 *     line worth having in an error report, and one of the two that existed
 *     was the auth-response log U-13 deleted from `auth-client.ts`. A future
 *     diagnostic that genuinely needs a breadcrumb should call
 *     `Sentry.addBreadcrumb` with named fields, so what is sent is chosen
 *     rather than swept up.
 *
 *  2. A fetch or XHR breadcrumb for an authentication path keeps only the fact
 *     that the call happened and how it ended. The address itself is redacted
 *     because an allauth URL can carry a key or an email in its path, and
 *     everything else the integration attached is dropped.
 *
 * Lives in its own module rather than inline in `main.tsx` so it can be tested:
 * importing `main.tsx` mounts the whole application.
 *
 * @param breadcrumb - the breadcrumb Sentry is about to record
 * @returns the breadcrumb to record, or null to drop it entirely
 */
export function beforeBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  if (breadcrumb.category === "console") {
    return null;
  }

  const url = (breadcrumb.data as { url?: unknown } | undefined)?.url;
  if (
    (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") &&
    typeof url === "string" &&
    url.includes("/_allauth/")
  ) {
    const data = breadcrumb.data as {
      method?: unknown;
      status_code?: unknown;
    };
    return {
      ...breadcrumb,
      data: {
        url: "/_allauth/[redacted]",
        method: data?.method,
        status_code: data?.status_code,
      },
    };
  }

  return breadcrumb;
}
